import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { CampaignsForm } from "@/components/tools/CampaignsForm";

import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS, MIXPANEL_SUBSCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { CreateOrUpdateCampaignRequest } from "@/types";

export const ToolsCampaignsEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("No ID found in URL for campaign edit page!");
  }

  const { data: campaign, status } =
    novaClient.internal.admin.campaigns.findOne.useQuery({
      id,
    });

  const { mutate: updateCampaign, isPending } =
    novaClient.internal.admin.campaigns.update.useMutation({
      onSuccess: () => {
        void navigate(`/tools/campaigns/${id}`);
      },
    });

  useSetTitle({
    title: "Tools | Campaigns | Edit",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.CAMPAIGNS, {
      subScreenName: MIXPANEL_SUBSCREENS.EDIT,
    });
  }, []);

  const handleSubmit = (data: CreateOrUpdateCampaignRequest) => {
    updateCampaign({ id, ...data });
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle className="!text-slate-500">
          <Link className="hover:underline" to="/tools/campaigns">
            Campaigns
          </Link>{" "}
          /{" "}
          <Link className="hover:underline" to={`/tools/campaigns/${id}`}>
            {id}
          </Link>{" "}
          / <span className="text-slate-700">Edit</span>
        </TopbarTitle>
      </TopbarContainer>
      <div className="flex-1 overflow-y-auto p-6">
        {status === "pending" && <LoadingIndicator variant="medium" />}
        {status === "error" && (
          <ErrorText errorMessage="Error loading campaign." />
        )}
        {status === "success" && (
          <CampaignsForm
            campaign={campaign}
            isSubmitting={isPending}
            onSubmit={handleSubmit}
            submitButtonText="Update"
          />
        )}
      </div>
    </Column>
  );
};
